import { writeStored } from "../lib/data-client.mjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Cloud,
  CloudFog,
  CloudRain,
  CloudSun,
  Loader2,
  MapPin,
  Snowflake,
  Sun,
  Wind,
  X,
} from "lucide-react";

const HOUR_MS = 60 * 60 * 1000;
const WEATHER_FETCH_TIMEOUT_MS = 8000;
const POSTAL_STORAGE_KEY = "neutral-weather-postal";
const CACHE_STORAGE_KEY = "neutral-weather-cache";
const LOCATION_STORAGE_KEY = "neutral-weather-location";

const weatherText = {
  0: "Klart",
  1: "Mest klart",
  2: "Let skyet",
  3: "Skyet",
  45: "Tåge",
  48: "Rimtåge",
  51: "Let støvregn",
  53: "Støvregn",
  55: "Kraftig støvregn",
  56: "Isslag",
  57: "Kraftigt isslag",
  61: "Let regn",
  63: "Regn",
  65: "Kraftig regn",
  66: "Underafkølet regn",
  67: "Kraftig underafkølet regn",
  71: "Let sne",
  73: "Sne",
  75: "Kraftig sne",
  77: "Snefnug",
  80: "Lette byger",
  81: "Byger",
  82: "Kraftige byger",
  85: "Snebyger",
  86: "Kraftige snebyger",
  95: "Torden",
  96: "Torden og hagl",
  99: "Kraftig torden",
};

function isValidPostal(value) {
  return /^\d{4}$/.test(value);
}

function readSavedPostal() {
  try {
    return localStorage.getItem(POSTAL_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function startOfHour(ms) {
  const date = new Date(ms);
  date.setMinutes(0, 0, 0);
  return date.getTime();
}

function readCachedWeather(postal) {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_STORAGE_KEY) || "{}");
    const entry = cache[postal];
    if (!entry || startOfHour(entry.cached_at) !== startOfHour(Date.now())) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function writeCachedWeather(postal, data) {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_STORAGE_KEY) || "{}");
    cache[postal] = { data, cached_at: Date.now() };
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage can fail in private browsing; weather still works for this view.
  }
}

function readSavedLocation() {
  try {
    const location = JSON.parse(localStorage.getItem(LOCATION_STORAGE_KEY) || "null");
    if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon)) return null;
    return location;
  } catch {
    return null;
  }
}

function writeSavedLocation(location) {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
  } catch {
    // Ignore storage errors; the current weather view still works.
  }
}

function clearSavedLocation() {
  try {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch {
    // Ignore storage errors.
  }
}

// Milliseconds until the next full local hour (12:00, 13:00, ...), plus a small
// jitter so visitors do not all hit the weather APIs in the same second.
function msUntilNextFullHour(now = Date.now()) {
  const next = new Date(now);
  next.setMinutes(0, 0, 0);
  return next.getTime() + HOUR_MS - now + Math.floor(Math.random() * 5000);
}

function weatherIcon(code) {
  if ([0, 1].includes(code)) return Sun;
  if ([2].includes(code)) return CloudSun;
  if ([45, 48].includes(code)) return CloudFog;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return Snowflake;
  if (code >= 51 && code <= 99) return CloudRain;
  return Cloud;
}

async function fetchJsonWithTimeout(url, timeoutMs = WEATHER_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchWeatherByPostal(postal) {
  const postalData = await fetchJsonWithTimeout(`https://api.zippopotam.us/dk/${postal}`);
  const place = postalData?.places?.[0];
  const lat = Number(place?.latitude);
  const lon = Number(place?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) throw new Error("coords");

  return fetchWeatherForPoint(lat, lon, {
    postal,
    city: place["place name"],
  });
}

async function fetchWeatherForPoint(lat, lon, place) {
  try {
    return await fetchOpenMeteoWeatherForPoint(lat, lon, place);
  } catch {
    return fetchMetWeatherForPoint(lat, lon, place);
  }
}

async function fetchOpenMeteoWeatherForPoint(lat, lon, place) {
  const weatherUrl = new URL("https://api.open-meteo.com/v1/forecast");
  weatherUrl.searchParams.set("latitude", lat);
  weatherUrl.searchParams.set("longitude", lon);
  weatherUrl.searchParams.set("current", "temperature_2m,weather_code,wind_speed_10m");
  weatherUrl.searchParams.set("hourly", "temperature_2m,weather_code");
  weatherUrl.searchParams.set("forecast_days", "2");
  weatherUrl.searchParams.set("timezone", "Europe/Copenhagen");

  const weatherData = await fetchJsonWithTimeout(weatherUrl.toString());
  const current = weatherData.current;
  if (!current) throw new Error("current");

  return {
    postal: place.postal,
    city: place.city,
    temperature: Math.round(current.temperature_2m),
    wind: Math.round(current.wind_speed_10m),
    code: current.weather_code,
    condition: weatherText[current.weather_code] || "Vejr",
    forecast: hourlyForecast(weatherData, current.time),
    updated_at: current.time,
  };
}

async function fetchMetWeatherForPoint(lat, lon, place) {
  const weatherUrl = new URL("https://api.met.no/weatherapi/locationforecast/2.0/compact");
  weatherUrl.searchParams.set("lat", lat);
  weatherUrl.searchParams.set("lon", lon);

  const weatherData = await fetchJsonWithTimeout(weatherUrl.toString());
  const timeseries = weatherData?.properties?.timeseries || [];
  const current = timeseries[0];
  const details = current?.data?.instant?.details;
  if (!details) throw new Error("met-current");

  const code = metSymbolToWeatherCode(current.data?.next_1_hours?.summary?.symbol_code);
  return {
    postal: place.postal,
    city: place.city,
    temperature: Math.round(details.air_temperature),
    wind: Math.round((details.wind_speed || 0) * 3.6),
    code,
    condition: weatherText[code] || "Vejr",
    forecast: metHourlyForecast(timeseries, current.time),
    updated_at: current.time,
  };
}

function metSymbolToWeatherCode(symbol = "") {
  const value = symbol.toLowerCase();
  if (value.includes("thunder")) return 95;
  if (value.includes("snow")) return 73;
  if (value.includes("sleet")) return 67;
  if (value.includes("heavyrain")) return 65;
  if (value.includes("rainshowers")) return 80;
  if (value.includes("rain")) return 61;
  if (value.includes("fog")) return 45;
  if (value.includes("partlycloudy")) return 2;
  if (value.includes("cloudy")) return 3;
  if (value.includes("fair")) return 1;
  if (value.includes("clearsky")) return 0;
  return 3;
}

function hourlyForecast(weatherData, currentTime) {
  const hourly = weatherData.hourly || {};
  const times = hourly.time || [];
  const temperatures = hourly.temperature_2m || [];
  const codes = hourly.weather_code || [];
  const currentMs = Date.parse(currentTime);
  if (!Number.isFinite(currentMs) || !times.length) return [];

  return [3, 6, 9]
    .map((hour) => {
      const targetMs = currentMs + hour * 60 * 60 * 1000;
      const index = times.findIndex((time) => Date.parse(time) >= targetMs);
      if (index < 0 || temperatures[index] == null || codes[index] == null) return null;
      return {
        hour,
        temperature: Math.round(temperatures[index]),
        code: codes[index],
      };
    })
    .filter(Boolean);
}

function metHourlyForecast(timeseries, currentTime) {
  const currentMs = Date.parse(currentTime);
  if (!Number.isFinite(currentMs) || !timeseries.length) return [];

  return [3, 6, 9]
    .map((hour) => {
      const targetMs = currentMs + hour * 60 * 60 * 1000;
      const item = timeseries.find((entry) => Date.parse(entry.time) >= targetMs);
      const details = item?.data?.instant?.details;
      if (!details || details.air_temperature == null) return null;
      return {
        hour,
        temperature: Math.round(details.air_temperature),
        code: metSymbolToWeatherCode(item.data?.next_1_hours?.summary?.symbol_code),
      };
    })
    .filter(Boolean);
}

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("geolocation"));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      maximumAge: 10 * 60 * 1000,
      timeout: 10000,
    });
  });
}

async function fetchWeatherByLocation() {
  const position = await getCurrentPosition();
  const { latitude, longitude } = position.coords;
  // DAWA reverse geocoding was shut down (HTTP 410); coordinates are enough for weather.
  const place = { postal: "Lokation", city: "Din position" };

  const data = await fetchWeatherForPoint(latitude, longitude, place);
  return {
    data,
    location: {
      lat: latitude,
      lon: longitude,
      postal: place.postal,
      city: place.city,
    },
  };
}

export function WeatherWidget() {
  const [postal, setPostal] = useState(readSavedPostal);
  const [draftPostal, setDraftPostal] = useState(postal);
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState(postal ? "loading" : "idle");
  const [isEditing, setIsEditing] = useState(!postal);
  const [locationStatus, setLocationStatus] = useState("idle");

  const Icon = useMemo(() => weatherIcon(weather?.code), [weather?.code]);

  useEffect(() => {
    if (postal === "geo") {
      const savedLocation = readSavedLocation();
      if (!savedLocation) {
        setStatus("idle");
        setIsEditing(true);
        return undefined;
      }

      let ignore = false;
      setStatus("loading");
      fetchWeatherForPoint(savedLocation.lat, savedLocation.lon, {
        postal: savedLocation.postal || "Lokation",
        city: savedLocation.city || "Din position",
      })
        .then((data) => {
          if (ignore) return;
          setWeather(data);
          setStatus("ready");
        })
        .catch(() => {
          if (ignore) return;
          setStatus("error");
        });

      return () => {
        ignore = true;
      };
    }

    if (!isValidPostal(postal)) return undefined;

    let ignore = false;
    const cached = readCachedWeather(postal);
    if (cached) {
      // Cached weather from this full hour is current; the hourly timer refreshes it.
      setWeather(cached);
      setStatus("ready");
      return undefined;
    }
    setStatus("loading");

    fetchWeatherByPostal(postal)
      .then((data) => {
        if (ignore) return;
        writeCachedWeather(postal, data);
        setWeather(data);
        setStatus("ready");
      })
      .catch(() => {
        if (ignore) return;
        setStatus("error");
      });

    return () => {
      ignore = true;
    };
  }, [postal]);

  const weatherRef = useRef(weather);
  weatherRef.current = weather;
  const weatherPostal = weather?.postal;

  useEffect(() => {
    if (!weatherPostal || status !== "ready" || isEditing) return undefined;

    let ignore = false;
    let timer;
    const refreshWeather = () => {
      const current = weatherRef.current;
      if (!current) return;
      if (isValidPostal(current.postal)) {
        fetchWeatherByPostal(current.postal)
          .then((data) => {
            if (ignore) return;
            writeCachedWeather(data.postal, data);
            setWeather(data);
            setStatus("ready");
          })
          .catch(() => {
            if (!ignore) setStatus("ready");
          });
        return;
      }

      const savedLocation = readSavedLocation();
      if (!savedLocation) return;
      fetchWeatherForPoint(savedLocation.lat, savedLocation.lon, {
        postal: savedLocation.postal || "Lokation",
        city: savedLocation.city || "Din position",
      })
        .then((data) => {
          if (ignore) return;
          setWeather(data);
          setStatus("ready");
        })
        .catch(() => {
          if (!ignore) setStatus("ready");
        });
    };

    // Refresh at every full hour (12:00, 13:00, ...), then schedule the next one.
    const scheduleNextHour = () => {
      timer = setTimeout(() => {
        refreshWeather();
        scheduleNextHour();
      }, msUntilNextFullHour());
    };
    scheduleNextHour();
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [weatherPostal, status, isEditing]);

  const savePostal = (event) => {
    event.preventDefault();
    const cleaned = draftPostal.replace(/\D/g, "").slice(0, 4);
    setDraftPostal(cleaned);
    if (!isValidPostal(cleaned)) return;
    clearSavedLocation();
    try { localStorage.setItem(POSTAL_STORAGE_KEY, cleaned); } catch {}
    setPostal(cleaned);
    setIsEditing(false);
  };

  const useLocationPostal = async () => {
    setLocationStatus("loading");
    setStatus("loading");
    try {
      const { data: locationWeather, location } = await fetchWeatherByLocation();
      writeSavedLocation(location);
      if (isValidPostal(locationWeather.postal)) {
        try { localStorage.setItem(POSTAL_STORAGE_KEY, locationWeather.postal); } catch {}
        setDraftPostal(locationWeather.postal);
        setPostal(locationWeather.postal);
        writeCachedWeather(locationWeather.postal, locationWeather);
      } else {
        writeStored(POSTAL_STORAGE_KEY, null);
        setDraftPostal("");
        setPostal("geo");
      }
      setWeather(locationWeather);
      setStatus("ready");
      setIsEditing(false);
      setLocationStatus("idle");
    } catch {
      setStatus(weather ? "ready" : "idle");
      setLocationStatus("error");
    }
  };

  const clearPostal = () => {
    writeStored(POSTAL_STORAGE_KEY, null);
    clearSavedLocation();
    setPostal("");
    setDraftPostal("");
    setWeather(null);
    setStatus("idle");
    setIsEditing(true);
  };

  return (
    <div className="px-6 py-3 border-t border-[#E5E5E5] dark:border-zinc-800">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <CloudSun className="w-3.5 h-3.5 text-[#81B29A]" strokeWidth={1.6} />
          <p className="text-xs tracking-[0.2em] uppercase font-mono text-gray-500 dark:text-gray-400">
            VEJR
          </p>
        </div>
        {postal && (
          <button
            type="button"
            onClick={clearPostal}
            className="text-gray-400 hover:text-[#0A0A0A] dark:hover:text-zinc-50 transition-colors rounded-sm focus:outline-none"
            aria-label="Fjern vejr"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.6} />
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={savePostal}>
          <label className="sr-only" htmlFor="weather-postal">
            Postnummer
          </label>
          <div className="grid grid-cols-[minmax(0,1fr)_32px_32px] gap-1.5">
            <input
              id="weather-postal"
              value={draftPostal}
              onChange={(event) => {
                setDraftPostal(event.target.value.replace(/\D/g, "").slice(0, 4));
                setLocationStatus("idle");
              }}
              inputMode="numeric"
              maxLength={4}
              placeholder="POSTNR."
              className="min-w-0 border border-[#E5E5E5] dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-[11px] font-mono tracking-[0.12em] text-[#0A0A0A] dark:text-zinc-100 outline-none placeholder-gray-400 dark:placeholder-gray-400"
              data-testid="weather-postal-input"
            />
            <button
              type="button"
              onClick={useLocationPostal}
              disabled={locationStatus === "loading"}
              className="inline-flex items-center justify-center border border-[#E5E5E5] dark:border-zinc-700 text-gray-500 dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-zinc-50 disabled:opacity-50 transition-colors rounded-sm focus:outline-none"
              aria-label="Brug min lokation"
              title="Brug min lokation"
              data-testid="weather-location-btn"
            >
              {locationStatus === "loading" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.6} />
              ) : (
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.6} />
              )}
            </button>
            <button
              type="submit"
              disabled={!isValidPostal(draftPostal)}
              className="inline-flex items-center justify-center border border-[#E5E5E5] dark:border-zinc-700 text-gray-500 dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-zinc-50 disabled:opacity-40 transition-colors rounded-sm focus:outline-none"
              aria-label="Gem postnummer"
              data-testid="weather-save-btn"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={1.6} />
            </button>
          </div>
          {locationStatus === "error" && (
            <p className="mt-2 text-[10px] tracking-[0.14em] uppercase font-mono text-gray-400">
              Lokation kunne ikke bruges
            </p>
          )}
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="w-full text-left rounded-sm focus:outline-none"
          data-testid="weather-summary"
        >
          {status === "loading" && (
            <p className="text-[10px] tracking-[0.16em] uppercase font-mono text-gray-400">
              HENTER VEJR
            </p>
          )}
          {status === "error" && (
            <p className="text-[10px] tracking-[0.16em] uppercase font-mono text-gray-400">
              VEJR IKKE FUNDET
            </p>
          )}
          {weather && status === "ready" && (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.16em] uppercase font-mono text-gray-400 truncate">
                    {weather.postal} · {weather.city}
                  </p>
                  <p className="text-sm font-semibold text-[#0A0A0A] dark:text-zinc-100 leading-tight mt-1">
                    {weather.condition}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#81B29A]" strokeWidth={1.7} />
                  <span className="text-2xl font-black font-['Archivo'] text-[#0A0A0A] dark:text-zinc-100">
                    {weather.temperature}°
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-[10px] tracking-[0.12em] uppercase font-mono text-gray-400">
                <Wind className="w-3 h-3" strokeWidth={1.5} />
                Vind {weather.wind} km/t
              </div>
              {weather.forecast?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {weather.forecast.map((item) => {
                    const ForecastIcon = weatherIcon(item.code);
                    return (
                      <div
                        key={item.hour}
                        className="flex items-center justify-between gap-1 border-t border-[#E5E5E5] dark:border-zinc-800 pt-2"
                      >
                        <span className="text-[10px] tracking-[0.12em] uppercase font-mono text-gray-400">
                          {item.hour}T
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A0A0A] dark:text-zinc-100">
                          <ForecastIcon className="w-3 h-3 text-[#81B29A]" strokeWidth={1.6} />
                          {item.temperature}&deg;
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </button>
      )}
    </div>
  );
}
